/**
 * BoundaryConditionManager — 灵活边界规则编辑器
 * 支持嵌套 AND/OR/NOT 逻辑、连续型和分类型协变量规则、模板、批量操作
 */
import React, { useState, useEffect } from 'react';
import {
  Card, Table, Space, Button, Modal, Form, Select, Input, InputNumber,
  Switch, Tag, Alert, Popconfirm, Row, Col, Dropdown, message,
} from 'antd';
import { PlusOutlined, CopyOutlined, UpOutlined } from '@ant-design/icons';
import { adminAPI } from '../services/api';
import { POPULATION_CATEGORIES } from '../constants/populations';
import { formatErrorMessage } from '../utils/errorHandler';
import { getPopulationTagColor } from '../utils/clinicalTagPalette';

interface ContinuousBoundaryRule {
  id?: number;
  covariate_key: string;
  covariate_label?: string;
  operator: 'between' | '>=' | '<=' | '>' | '<' | '==' | '!=';
  min_value?: number | null;
  max_value?: number | null;
  unit?: string;
  description?: string;
}

const OPERATOR_OPTIONS: Array<{ value: ContinuousBoundaryRule['operator']; label: string }> = [
  { value: 'between', label: '介于（含上下限）' },
  { value: '>=', label: '大于等于 ≥' },
  { value: '>', label: '大于 >' },
  { value: '<=', label: '小于等于 ≤' },
  { value: '<', label: '小于 <' },
  { value: '==', label: '等于 =' },
  { value: '!=', label: '不等于 ≠' },
];

const canonicalizeRuleValues = (rule: ContinuousBoundaryRule): ContinuousBoundaryRule => {
  if (rule.operator === 'between') return rule;
  if (['<=', '<'].includes(rule.operator)) {
    return { ...rule, min_value: null, max_value: rule.max_value ?? rule.min_value ?? null };
  }
  return { ...rule, min_value: rule.min_value ?? rule.max_value ?? null, max_value: null };
};

const formatRuleCondition = (rule: ContinuousBoundaryRule): string => {
  const normalized = canonicalizeRuleValues(rule);
  const label = normalized.covariate_label || normalized.covariate_key || '协变量';
  const unit = normalized.unit ? ` ${normalized.unit}` : '';
  if (normalized.operator === 'between') {
    const lower = normalized.min_value ?? '?';
    const upper = normalized.max_value ?? '?';
    return `${label} ${lower}–${upper}${unit}`;
  }
  const threshold = ['<=', '<'].includes(normalized.operator)
    ? normalized.max_value
    : normalized.min_value;
  const operator = normalized.operator === '==' ? '=' : normalized.operator === '!=' ? '≠' : normalized.operator;
  return `${label} ${operator}${threshold ?? '?'}${unit}`;
};

const BoundaryConditionManager: React.FC<{ modelId: string }> = ({ modelId }) => {
  const [groups, setGroups] = useState<any[]>([]);
  const [covDefs, setCovDefs] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [groupForm] = Form.useForm();
  const [ruleForm] = Form.useForm();
  const [catForm] = Form.useForm();
  const [batchForm] = Form.useForm();
  const [groupModal, setGroupModal] = useState(false);
  const [ruleModal, setRuleModal] = useState<{ open: boolean; groupId?: number; editing?: any }>({ open: false });
  const [catModal, setCatModal] = useState<{ open: boolean; groupId?: number; editing?: any }>({ open: false });
  const [batchModal, setBatchModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<any>(null);
  const ruleOperator = Form.useWatch('operator', ruleForm) as ContinuousBoundaryRule['operator'] | undefined;

  const load = async () => {
    if (!modelId) return;
    const [g, c, t] = await Promise.all([
      adminAPI.getBoundaryGroups(modelId),
      adminAPI.getCovariateDefinitions(),
      adminAPI.getBoundaryTemplates(),
    ]);
    setGroups(g);
    setCovDefs(c);
    setTemplates(t);
  };

  useEffect(() => { load(); }, [modelId]);

  const selectedGroup = groups.find(g => g.id === selectedGroupId);

  const openAddGroup = () => { setEditingGroup(null); groupForm.resetFields(); setGroupModal(true); };
  const openEditGroup = (g: any) => {
    setEditingGroup(g);
    groupForm.setFieldsValue({
      group_name: g.group_name,
      logic_operator: g.logic_operator,
      is_negated: g.is_negated,
      priority: g.priority,
      description: g.description,
      population_tags: g.population_tags || [],
      group_type: g.group_type || 'standard',  // 新增：规则组类型
    });
    setGroupModal(true);
  };
  const saveGroup = async () => {
    const vals = await groupForm.validateFields();
    if (editingGroup) {
      await adminAPI.updateBoundaryGroup(editingGroup.id, vals);
    } else {
      await adminAPI.createBoundaryGroup(modelId, vals);
    }
    setGroupModal(false);
    load();
  };

  const openAddRule = (groupId: number) => {
    ruleForm.resetFields();
    ruleForm.setFieldsValue({ operator: 'between' });
    setRuleModal({ open: true, groupId });
  };
  const openEditRule = (groupId: number, rule: ContinuousBoundaryRule) => {
    ruleForm.setFieldsValue(canonicalizeRuleValues(rule));
    setRuleModal({ open: true, groupId, editing: rule });
  };
  const saveRule = async () => {
    try {
      const vals = canonicalizeRuleValues(await ruleForm.validateFields());
      if (ruleModal.editing) {
        await adminAPI.updateBoundaryRule(ruleModal.editing.id, vals);
      } else {
        await adminAPI.createBoundaryRule(ruleModal.groupId!, vals);
      }
      message.success(ruleModal.editing ? '边界规则已更新' : '边界规则已创建');
      setRuleModal({ open: false });
      load();
    } catch (err: unknown) {
      if (typeof err === 'object' && err !== null && 'errorFields' in err) return;
      message.error(formatErrorMessage(err, '边界规则保存失败'));
    }
  };

  const openAddCat = (groupId: number) => { catForm.resetFields(); setCatModal({ open: true, groupId }); };
  const openEditCat = (groupId: number, rule: any) => {
    catForm.setFieldsValue(rule);
    setCatModal({ open: true, groupId, editing: rule });
  };
  const saveCat = async () => {
    const vals = await catForm.validateFields();
    if (catModal.editing) {
      await adminAPI.updateCategoricalBoundary(catModal.editing.id, vals);
    } else {
      await adminAPI.createCategoricalBoundary(catModal.groupId!, vals);
    }
    setCatModal({ open: false });
    load();
  };

  const handleCreateFromTemplate = async (templateId: number) => {
    try {
      await adminAPI.createBoundaryGroupFromTemplate(modelId, templateId);
      message.success('从模板创建成功');
      load();
    } catch (err: any) {
      message.error(formatErrorMessage(err, '创建失败'));
    }
  };
  const openBatchModal = () => {
    batchForm.resetFields();
    setBatchModal(true);
  };
  const saveBatch = async () => {
    const vals = await batchForm.validateFields();
    const updates = Object.fromEntries(
      Object.entries(vals).filter(([_, v]) => v !== undefined && v !== null)
    );
    if (Object.keys(updates).length === 0) {
      message.warning('请至少修改一个字段');
      return;
    }
    try {
      await adminAPI.batchUpdateBoundaryGroups(selectedRowKeys, updates);
      message.success(`成功更新 ${selectedRowKeys.length} 个规则组`);
      setSelectedRowKeys([]);
      setBatchModal(false);
      load();
    } catch (err: any) {
      message.error(formatErrorMessage(err, '批量更新失败'));
    }
  };

  const ruleColumns = [
    { title: '协变量', dataIndex: 'covariate_key', render: (_: any, r: any) => `${r.covariate_label || r.covariate_key} (${r.covariate_key})` },
    { title: '操作符', dataIndex: 'operator', render: (v: string) => <Tag>{v}</Tag> },
    { title: '规则预览', key: 'condition', render: (_: unknown, rule: ContinuousBoundaryRule) => formatRuleCondition(rule) },
    {
      title: '操作', width: 120, render: (_: any, r: any) => (
        <Space>
          <Button size="small" onClick={() => openEditRule(selectedGroupId!, r)}>编辑</Button>
          <Popconfirm title="确定删除？" onConfirm={async () => { await adminAPI.deleteBoundaryRule(r.id); load(); }}>
            <Button size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      )
    },
  ];

  const catColumns = [
    { title: '协变量', dataIndex: 'covariate_key', render: (_: any, r: any) => `${r.covariate_label || r.covariate_key} (${r.covariate_key})` },
    { title: '允许值', dataIndex: 'allowed_values', render: (v: string[]) => v?.map((x, i) => <Tag key={i}>{x}</Tag>) },
    { title: '类型', dataIndex: 'is_exclusion', render: (v: boolean) => v ? <Tag color="red">排除</Tag> : <Tag color="green">包含</Tag> },
    {
      title: '操作', width: 120, render: (_: any, r: any) => (
        <Space>
          <Button size="small" onClick={() => openEditCat(selectedGroupId!, r)}>编辑</Button>
          <Popconfirm title="确定删除？" onConfirm={async () => { await adminAPI.deleteCategoricalBoundary(r.id); load(); }}>
            <Button size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      )
    },
  ];
  return (
    <>
      <Card size="small" title="边界规则组" extra={
        <Space>
          {templates.length > 0 && (
            <Dropdown menu={{
              items: templates.map(t => ({
                key: t.id,
                label: `${t.template_name} (${t.population_tags.join(', ')})`,
                onClick: () => handleCreateFromTemplate(t.id)
              }))
            }}>
              <Button size="small" icon={<CopyOutlined />}>从模板创建</Button>
            </Dropdown>
          )}
          <Button size="small" icon={<PlusOutlined />} onClick={openAddGroup}>新增规则组</Button>
        </Space>
      }>
        {groups.length > 0 && !selectedGroupId && (
          <div style={{ padding: '8px 0', color: '#1890ff', fontSize: '13px' }}>
            点击下方表格中的规则组，即可添加和编辑规则
          </div>
        )}
        {selectedRowKeys.length > 0 && (
          <Alert
            message={
              <Space>
                <span>已选择 {selectedRowKeys.length} 个规则组</span>
                <Button size="small" onClick={openBatchModal}>批量设置</Button>
                <Button size="small" onClick={() => setSelectedRowKeys([])}>取消选择</Button>
              </Space>
            }
            type="info"
            closable
            style={{ marginBottom: 12 }}
          />
        )}
        <Table size="small" dataSource={groups} rowKey="id" pagination={false}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys as number[])
          }}
          onRow={g => ({
            onClick: () => setSelectedGroupId(prev => prev === g.id ? null : g.id),
            style: { cursor: 'pointer', background: g.id === selectedGroupId ? '#e6f4ff' : undefined }
          })}
          columns={[
            { title: '名称', dataIndex: 'group_name', width: 150 },
            {
              title: '类型', dataIndex: 'group_type', width: 90,
              render: (type: string) => (
                type === 'exclusion'
                  ? <Tag color="red">排除标准</Tag>
                  : <Tag color="blue">标准规则</Tag>
              )
            },
            {
              title: '人群标签', dataIndex: 'population_tags', width: 200,
              render: (tags: string[]) => (
                <Space wrap>
                  {tags && tags.length > 0 ? tags.map((tag, i) => <Tag key={i} className="clinical-entity-tag" color={getPopulationTagColor(tag)}>{tag}</Tag>) : <Tag color="default">全部人群</Tag>}
                </Space>
              )
            },
            { title: '逻辑', dataIndex: 'logic_operator', width: 70, render: (v: string) => <Tag color={v === 'OR' ? 'green' : 'blue'}>{v}</Tag> },
            { title: '取反', dataIndex: 'is_negated', width: 60, render: (v: boolean) => v ? <Tag color="red">NOT</Tag> : null },
            { title: '优先级', dataIndex: 'priority', width: 70 },
            {
              title: '操作', width: 130, render: (_: any, g: any) => (
                <Space onClick={e => e.stopPropagation()}>
                  <Button size="small" onClick={() => openEditGroup(g)}>编辑</Button>
                  <Popconfirm title="确定删除此规则组及其所有规则？" onConfirm={async () => { await adminAPI.deleteBoundaryGroup(g.id); if (selectedGroupId === g.id) setSelectedGroupId(null); load(); }}>
                    <Button size="small" danger>删除</Button>
                  </Popconfirm>
                </Space>
              )
            },
          ]}
        />
      </Card>
      {selectedGroup && (
        <>
          <Card size="small"
            title={<Space><span>连续型规则 — {selectedGroup.group_name}</span><Button size="small" type="text" icon={<UpOutlined />} onClick={() => setSelectedGroupId(null)}>收起</Button></Space>}
            style={{ marginTop: 12 }}
            extra={<Button size="small" icon={<PlusOutlined />} onClick={() => openAddRule(selectedGroupId!)}>新增规则</Button>}>
            <Table size="small" dataSource={selectedGroup.rules} rowKey="id" pagination={false} columns={ruleColumns} />
          </Card>
          <Card size="small" title="分类型规则" style={{ marginTop: 12 }}
            extra={<Button size="small" icon={<PlusOutlined />} onClick={() => openAddCat(selectedGroupId!)}>新增分类规则</Button>}>
            <Table size="small" dataSource={selectedGroup.categorical_rules} rowKey="id" pagination={false} columns={catColumns} />
          </Card>
        </>
      )}

      {/* 规则组编辑 */}
      <Modal title={editingGroup ? '编辑规则组' : '新增规则组'} open={groupModal} onOk={saveGroup} onCancel={() => setGroupModal(false)}>
        <Form form={groupForm} layout="vertical">
          <Form.Item name="group_name" label="名称" rules={[{ required: true }]}><Input placeholder="例如：新生儿基础条件" /></Form.Item>
          <Form.Item name="group_type" label="规则组类型" initialValue="standard">
            <Select>
              <Select.Option value="standard">标准边界规则（软匹配，影响评分）</Select.Option>
              <Select.Option value="exclusion">排除标准（硬过滤，不满足直接排除模型）</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="population_tags" label="适用人群标签" tooltip="选择此规则组适用的人群。留空表示适用于所有人群。支持多选。">
            <Select mode="multiple" placeholder="选择人群标签（可多选）" allowClear
              options={POPULATION_CATEGORIES.map(p => ({ label: `${p.label} (${p.ageHint})`, value: p.key }))} />
          </Form.Item>
          <Form.Item name="logic_operator" label="逻辑操作符" initialValue="AND">
            <Select options={[{ value: 'AND', label: 'AND（所有规则满足）' }, { value: 'OR', label: 'OR（任一规则满足）' }]} />
          </Form.Item>
          <Form.Item name="is_negated" label="取反(NOT)" valuePropName="checked" initialValue={false}><Switch /></Form.Item>
          <Form.Item name="priority" label="优先级" initialValue={1}><InputNumber min={1} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="description" label="描述"><Input.TextArea rows={2} placeholder="规则组的详细说明" /></Form.Item>
        </Form>
      </Modal>
      {/* 连续型规则编辑 */}
      <Modal title={ruleModal.editing ? '编辑规则' : '新增规则'} open={ruleModal.open} onOk={saveRule} onCancel={() => setRuleModal({ open: false })} width={720}>
        <Form form={ruleForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="covariate_key" label="协变量" rules={[{ required: true }]}>
                <Select showSearch placeholder="选择协变量"
                  onChange={v => { const d = covDefs.find((c: any) => c.key === v); if (d) ruleForm.setFieldsValue({ covariate_label: d.label, unit: d.unit }); }}>
                  {covDefs.map((c: any) => <Select.Option key={c.key} value={c.key}>{c.label} ({c.key})</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}><Form.Item name="covariate_label" label="显示名称"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="operator" label="操作符" rules={[{ required: true }]}>
                <Select
                  options={OPERATOR_OPTIONS}
                  onChange={() => ruleForm.setFieldsValue({ min_value: undefined, max_value: undefined })}
                />
              </Form.Item>
            </Col>
            {ruleOperator === 'between' ? (
              <>
                <Col span={5}>
                  <Form.Item name="min_value" label="下限" rules={[{ required: true, message: '请填写下限' }]} preserve={false}>
                    <InputNumber style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <Form.Item
                    name="max_value"
                    label="上限"
                    dependencies={['min_value']}
                    preserve={false}
                    rules={[
                      { required: true, message: '请填写上限' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const lower = getFieldValue('min_value');
                          return lower == null || value == null || Number(value) >= Number(lower)
                            ? Promise.resolve()
                            : Promise.reject(new Error('上限不能小于下限'));
                        },
                      }),
                    ]}
                  >
                    <InputNumber style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </>
            ) : ['<=', '<'].includes(ruleOperator || '') ? (
              <Col span={10}>
                <Form.Item name="max_value" label="阈值" rules={[{ required: true, message: '请填写阈值' }]} preserve={false}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            ) : (
              <Col span={10}>
                <Form.Item name="min_value" label={['==', '!='].includes(ruleOperator || '') ? '目标值' : '阈值'} rules={[{ required: true, message: '请填写阈值' }]} preserve={false}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            )}
            <Col span={6}><Form.Item name="unit" label="单位"><Input /></Form.Item></Col>
          </Row>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldsValue }) => {
              const values = getFieldsValue() as ContinuousBoundaryRule;
              return values.operator && values.covariate_key ? (
                <Alert
                  type="info"
                  showIcon
                  message="运行时规则预览"
                  description={formatRuleCondition(values)}
                  style={{ marginBottom: 16 }}
                />
              ) : null;
            }}
          </Form.Item>
          <Form.Item name="description" label="描述"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>

      {/* 分类型规则编辑 */}
      <Modal title={catModal.editing ? '编辑分类规则' : '新增分类规则'} open={catModal.open} onOk={saveCat} onCancel={() => setCatModal({ open: false })}>
        <Form form={catForm} layout="vertical">
          <Form.Item name="covariate_key" label="协变量" rules={[{ required: true }]}>
            <Select showSearch placeholder="选择协变量"
              onChange={v => { const d = covDefs.find((c: any) => c.key === v); if (d) catForm.setFieldsValue({ covariate_label: d.label }); }}>
              {covDefs.map((c: any) => <Select.Option key={c.key} value={c.key}>{c.label} ({c.key})</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="covariate_label" label="显示名称"><Input /></Form.Item>
          <Form.Item name="allowed_values" label="值列表" rules={[{ required: true }]}>
            <Select mode="tags" placeholder="输入后按回车添加" />
          </Form.Item>
          <Form.Item name="is_exclusion" label="是否排除" valuePropName="checked" initialValue={false}><Switch checkedChildren="排除" unCheckedChildren="包含" /></Form.Item>
          <Form.Item name="description" label="描述"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>

      {/* 批量操作 Modal */}
      <Modal title="批量设置规则组" open={batchModal} onOk={saveBatch} onCancel={() => setBatchModal(false)}>
        <Form form={batchForm} layout="vertical">
          <Alert message={`将更新 ${selectedRowKeys.length} 个规则组`} type="info" style={{ marginBottom: 16 }} />
          <Form.Item name="population_tags" label="人群标签">
            <Select mode="multiple" placeholder="选择人群标签（可多选）" allowClear
              options={POPULATION_CATEGORIES.map(p => ({ label: `${p.label} (${p.ageHint})`, value: p.key }))} />
          </Form.Item>
          <Form.Item name="priority" label="优先级">
            <InputNumber min={1} style={{ width: '100%' }} placeholder="留空表示不修改" />
          </Form.Item>
          <Form.Item name="logic_operator" label="逻辑操作符">
            <Select placeholder="留空表示不修改" allowClear options={[
              { value: 'AND', label: 'AND（所有规则满足）' },
              { value: 'OR', label: 'OR（任一规则满足）' }
            ]} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default BoundaryConditionManager;
